/**
 * Leaving a half-checked segmented item (pedagogy spec §4.3.4, "Leaving mid-item"). Checking a
 * part is stateless, so a student can see a ✗ on (a) and walk away with nothing recorded — the
 * unaided first try the evidence measures. Navigating away within the app therefore sends what was
 * checked, with `offerRetry: false` (the solution is not wanted at once) and the unchecked parts
 * empty. Closing the tab still loses the try; that risk is named in the spec (§4.7).
 *
 * The page has unmounted by the time the answer comes back, so the result is folded into the
 * stored session instead of component state: coming back shows the solution, not a second try.
 */
import { api } from "@client/api/client";
import { isPending, terminalPatch } from "./retryFlow";
import {
  loadSession,
  saveSession,
  SESSION_PATCHED_EVENT,
  type QuestionResult,
  type SessionPatchedDetail,
} from "./session";
import type { AnswerBody, AnswerResponse } from "./types";

/**
 * Merges `patch` into a question's stored result when no page is left to hold the state. A late
 * response goes through here, so coming back shows the answered item instead of a second chance
 * that would record the same try twice. `clearConfidence` drops the rating, as on the page.
 *
 * `sessionId` is the session the answer was sent from: the stored session is patched only if it
 * is that one, so an answer can never land on a newer session that holds the same question. It is
 * undefined for a session saved before ids existed, which cannot be told apart and is patched as
 * before. A patch is announced on `window` (`SESSION_PATCHED_EVENT`) so a page that has been
 * mounted since, and loaded the session before the answer came back, takes the result over.
 */
export function patchStoredResult(
  questionId: string,
  patch: Partial<QuestionResult>,
  options: { sessionId?: string; clearConfidence?: boolean; skipIfAnswered?: boolean } = {},
): void {
  const stored = loadSession();
  const existing = stored?.results[questionId];
  if (!stored || !existing) return;
  if (options.sessionId !== undefined && stored.id !== options.sessionId) return;
  if (options.skipIfAnswered && existing.answered) return;
  const merged = { ...existing, ...patch } as QuestionResult;
  if (options.clearConfidence) delete merged.confidence;
  saveSession({ ...stored, results: { ...stored.results, [questionId]: merged } });
  const detail: SessionPatchedDetail = {
    questionId,
    ...(stored.id !== undefined ? { sessionId: stored.id } : {}),
  };
  window.dispatchEvent(new CustomEvent(SESSION_PATCHED_EVENT, { detail }));
}

/** `sessionId`: the session the page was running, so the answer finds its own session again. */
export async function sendLeaveAnswer(body: AnswerBody, sessionId?: string): Promise<void> {
  try {
    const res = await api.post<AnswerResponse>("/api/practice/answer", body);
    // `offerRetry: false` is never withheld; a server that did would leave the item for a retry.
    if (isPending(res)) return;
    patchStoredResult(
      body.questionId,
      {
        ...terminalPatch(res, { numericRaw: body.answer.numeric ?? {} }),
        ...(body.confidence !== undefined ? { confidence: body.confidence } : {}),
      },
      { skipIfAnswered: true, ...(sessionId !== undefined ? { sessionId } : {}) },
    );
  } catch {
    /* the try is lost, as a closed tab would lose it; there is nobody left to tell */
  }
}
